import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import type {
  ActorActionResult,
  CreatedActorMapping,
  CreatedComponentMapping,
  CreateManyCounts,
} from './editor-api-types';

import { nameHelpers, objectActions } from '@/editor/core';
import { setPropertyValueDirect, UniversalAccessor } from '@/editor/core/universalAccessor';
import { assetsStore, editorStore } from '@/editor/store';


/** Supported bulk-create payload version. */
export const CREATE_MANY_PAYLOAD_VERSION = 1;

export const CREATE_MANY_MAX_ACTORS = 500;
export const CREATE_MANY_MAX_TEMPLATES = 100;
export const CREATE_MANY_MAX_COMPONENTS_PER_ACTOR = 32;
export const CREATE_MANY_MAX_PROPERTIES_PER_COMPONENT = 64;
export const CREATE_MANY_MAX_PAYLOAD_BYTES = 2_000_000;

type ResolvedComponentSpec = {
  key: string;
  className: string;
  properties: Record<string, unknown>;
  parentComponentId?: string;
};

type ResolvedActorSpec = {
  key: string;
  className: string;
  name?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  components: ResolvedComponentSpec[];
};

type ParsedPayload = {
  templates: Record<string, { className: string; components: ResolvedComponentSpec[] }>;
  actors: ResolvedActorSpec[];
  counts: CreateManyCounts;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function parseNumberTriple(raw: unknown, label: string, errors: string[]): [number, number, number] | undefined {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw) || raw.length < 3) {
    errors.push(`${label} must be a three-number array [x, y, z].`);
    return undefined;
  }
  const [a, b, c] = raw;
  if (
    typeof a !== 'number' || !Number.isFinite(a)
    || typeof b !== 'number' || !Number.isFinite(b)
    || typeof c !== 'number' || !Number.isFinite(c)
  ) {
    errors.push(`${label} must contain finite numbers.`);
    return undefined;
  }
  return [a, b, c];
}

function parseProperties(
  raw: unknown,
  label: string,
  errors: string[],
): Record<string, unknown> | null {
  if (raw === undefined) return {};
  if (!isRecord(raw)) {
    errors.push(`${label} must be an object.`);
    return null;
  }
  const entries = Object.entries(raw);
  if (entries.length > CREATE_MANY_MAX_PROPERTIES_PER_COMPONENT) {
    errors.push(`${label} exceeds the limit of ${CREATE_MANY_MAX_PROPERTIES_PER_COMPONENT} properties.`);
    return null;
  }
  const properties: Record<string, unknown> = {};
  for (const [path, value] of entries) {
    if (!path.trim()) {
      errors.push(`${label} contains an empty property path.`);
      continue;
    }
    properties[path] = value;
  }
  return properties;
}

function parseComponentSpec(
  raw: unknown,
  label: string,
  errors: string[],
): ResolvedComponentSpec | null {
  if (!isRecord(raw)) {
    errors.push(`${label} must be an object.`);
    return null;
  }
  const key = typeof raw['key'] === 'string' ? raw['key'].trim() : '';
  const className = typeof raw['className'] === 'string' ? raw['className'].trim() : '';
  if (!key) {
    errors.push(`${label} requires a non-empty key.`);
    return null;
  }
  if (!className) {
    errors.push(`${label} requires className.`);
    return null;
  }
  const properties = parseProperties(raw['properties'], `${label}.properties`, errors);
  if (properties === null) return null;
  const parentComponentId = typeof raw['parentComponentId'] === 'string'
    ? raw['parentComponentId'].trim()
    : undefined;
  return { key, className, properties, parentComponentId };
}

function mergeProperties(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  return { ...base, ...override };
}

function isKnownActorClass(className: string): boolean {
  return assetsStore.actorClassMap.has(className);
}

function isKnownComponentClass(className: string): boolean {
  return assetsStore.componentClassMap.has(className);
}

/**
 * Validates and normalises a createMany payload into resolved actor/component plans.
 */
export function parseCreateManyPayload(payload: unknown):
  | { ok: true; value: ParsedPayload }
  | { ok: false; errors: string[] } {
  const errors: string[] = [];

  if (!isRecord(payload)) {
    return { ok: false, errors: ['payload must be an object.'] };
  }

  const serialised = JSON.stringify(payload);
  if (serialised.length > CREATE_MANY_MAX_PAYLOAD_BYTES) {
    return {
      ok: false,
      errors: [`payload exceeds the ${CREATE_MANY_MAX_PAYLOAD_BYTES} byte limit.`],
    };
  }

  const version = payload['version'];
  if (version !== CREATE_MANY_PAYLOAD_VERSION) {
    errors.push(`payload.version must be ${CREATE_MANY_PAYLOAD_VERSION}.`);
  }

  const templatesRaw = payload['templates'];
  const templates: ParsedPayload['templates'] = {};
  if (templatesRaw !== undefined) {
    if (!isRecord(templatesRaw)) {
      errors.push('payload.templates must be an object when provided.');
    } else {
      const templateEntries = Object.entries(templatesRaw);
      if (templateEntries.length > CREATE_MANY_MAX_TEMPLATES) {
        errors.push(`payload.templates exceeds the limit of ${CREATE_MANY_MAX_TEMPLATES} templates.`);
      }
      for (const [templateKey, templateValue] of templateEntries) {
        if (!templateKey.trim()) {
          errors.push('payload.templates contains an empty template key.');
          continue;
        }
        if (!isRecord(templateValue)) {
          errors.push(`payload.templates.${templateKey} must be an object.`);
          continue;
        }
        const className = typeof templateValue['className'] === 'string'
          ? templateValue['className'].trim() || 'Actor'
          : 'Actor';
        const componentsRaw = templateValue['components'];
        const components: ResolvedComponentSpec[] = [];
        if (componentsRaw !== undefined) {
          if (!Array.isArray(componentsRaw)) {
            errors.push(`payload.templates.${templateKey}.components must be an array.`);
          } else {
            if (componentsRaw.length > CREATE_MANY_MAX_COMPONENTS_PER_ACTOR) {
              errors.push(`payload.templates.${templateKey}.components exceeds the per-actor component limit.`);
            }
            const seenKeys = new Set<string>();
            for (let index = 0; index < componentsRaw.length; index += 1) {
              const parsed = parseComponentSpec(
                componentsRaw[index],
                `payload.templates.${templateKey}.components[${index}]`,
                errors,
              );
              if (!parsed) continue;
              if (seenKeys.has(parsed.key)) {
                errors.push(`payload.templates.${templateKey} contains duplicate component key "${parsed.key}".`);
                continue;
              }
              seenKeys.add(parsed.key);
              components.push(parsed);
            }
          }
        }
        templates[templateKey] = { className, components };
      }
    }
  }

  const actorsRaw = payload['actors'];
  if (!Array.isArray(actorsRaw)) {
    errors.push('payload.actors must be a non-empty array.');
    return { ok: false, errors };
  }
  if (actorsRaw.length === 0) {
    errors.push('payload.actors must contain at least one actor entry.');
  }
  if (actorsRaw.length > CREATE_MANY_MAX_ACTORS) {
    errors.push(`payload.actors exceeds the limit of ${CREATE_MANY_MAX_ACTORS} actors.`);
  }

  const actors: ResolvedActorSpec[] = [];
  const actorKeys = new Set<string>();
  let totalComponents = 0;
  let totalProperties = 0;

  for (let index = 0; index < actorsRaw.length; index += 1) {
    const actorRaw = actorsRaw[index];
    const label = `payload.actors[${index}]`;
    if (!isRecord(actorRaw)) {
      errors.push(`${label} must be an object.`);
      continue;
    }

    const key = typeof actorRaw['key'] === 'string' ? actorRaw['key'].trim() : '';
    if (!key) {
      errors.push(`${label} requires a non-empty key.`);
      continue;
    }
    if (actorKeys.has(key)) {
      errors.push(`${label} duplicates actor key "${key}".`);
      continue;
    }
    actorKeys.add(key);

    const templateName = typeof actorRaw['template'] === 'string' ? actorRaw['template'].trim() : '';
    const inlineClassName = typeof actorRaw['className'] === 'string' ? actorRaw['className'].trim() : '';
    let className = inlineClassName || 'Actor';
    let baseComponents: ResolvedComponentSpec[] = [];

    if (templateName) {
      const template = templates[templateName];
      if (!template) {
        errors.push(`${label} references unknown template "${templateName}".`);
        continue;
      }
      className = inlineClassName || template.className;
      baseComponents = template.components.map(component => ({
        ...component,
        properties: { ...component.properties },
      }));
    }

    const componentsRaw = actorRaw['components'];
    if (componentsRaw !== undefined) {
      if (Array.isArray(componentsRaw)) {
        if (!templateName && componentsRaw.length === 0) {
          errors.push(`${label} must include components or reference a template.`);
        }
        const componentMap = new Map(baseComponents.map(component => [component.key, component]));
        for (let componentIndex = 0; componentIndex < componentsRaw.length; componentIndex += 1) {
          const parsed = parseComponentSpec(
            componentsRaw[componentIndex],
            `${label}.components[${componentIndex}]`,
            errors,
          );
          if (!parsed) continue;
          componentMap.set(parsed.key, parsed);
        }
        baseComponents = Array.from(componentMap.values());
      } else if (isRecord(componentsRaw)) {
        for (const [componentKey, overrideValue] of Object.entries(componentsRaw)) {
          const existing = baseComponents.find(component => component.key === componentKey);
          if (!existing) {
            errors.push(`${label}.components.${componentKey} overrides unknown component key.`);
            continue;
          }
          if (!isRecord(overrideValue)) {
            errors.push(`${label}.components.${componentKey} must be an object.`);
            continue;
          }
          const overrideProperties = parseProperties(
            overrideValue['properties'],
            `${label}.components.${componentKey}.properties`,
            errors,
          );
          if (overrideProperties === null) continue;
          existing.properties = mergeProperties(existing.properties, overrideProperties);
          if (typeof overrideValue['enabled'] === 'boolean') {
            existing.properties['enabled'] = overrideValue['enabled'];
          }
        }
      } else {
        errors.push(`${label}.components must be an array or override object.`);
        continue;
      }
    }

    if (!templateName && baseComponents.length === 0) {
      errors.push(`${label} must reference a template or include components.`);
    }

    if (baseComponents.length > CREATE_MANY_MAX_COMPONENTS_PER_ACTOR) {
      errors.push(`${label} exceeds the per-actor component limit of ${CREATE_MANY_MAX_COMPONENTS_PER_ACTOR}.`);
    }

    if (!isKnownActorClass(className)) {
      errors.push(`${label} references unknown actor class "${className}".`);
    }
    for (const component of baseComponents) {
      if (!isKnownComponentClass(component.className)) {
        errors.push(`${label} references unknown component class "${component.className}" for key "${component.key}".`);
      }
      totalProperties += Object.keys(component.properties).length;
    }

    totalComponents += baseComponents.length;
    actors.push({
      key,
      className,
      name: typeof actorRaw['name'] === 'string' ? actorRaw['name'].trim() : undefined,
      position: parseNumberTriple(actorRaw['position'], `${label}.position`, errors),
      rotation: parseNumberTriple(actorRaw['rotation'], `${label}.rotation`, errors),
      scale: parseNumberTriple(actorRaw['scale'], `${label}.scale`, errors),
      components: baseComponents,
    });
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    value: {
      templates,
      actors,
      counts: {
        actors: actors.length,
        components: totalComponents,
        properties: totalProperties,
      },
    },
  };
}

function buildTransform(spec: ResolvedActorSpec): {
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
  scale?: THREE.Vector3;
} {
  return {
    position: spec.position ? new THREE.Vector3(...spec.position) : undefined,
    rotation: spec.rotation ? new THREE.Euler(...spec.rotation) : undefined,
    scale: spec.scale ? new THREE.Vector3(...spec.scale) : undefined,
  };
}

function setComponentProperties(component: unknown, properties: Record<string, unknown>): string[] {
  const changed: string[] = [];
  for (const [path, value] of Object.entries(properties)) {
    if (!path.trim()) continue;
    if (setPropertyValueDirect(component, path, value)) {
      objectActions.notifyEditorPropertyChanged(component, path, value);
      changed.push(path);
    }
  }
  return changed;
}

function blockedCreateManyResult(
  mode: 'dryRun' | 'apply',
  errors: string[],
  counts?: CreateManyCounts,
): ActorActionResult {
  return {
    ok: false,
    action: 'createMany',
    mode,
    status: 'blocked',
    plannedChanges: [],
    affectedActorIds: [],
    missingActorIds: [],
    counts,
    createdActors: [],
    createdComponents: [],
    undoGroupId: null,
    undoUnavailableReason: errors[0] ?? 'Invalid createMany payload.',
    warnings: errors,
  };
}

/**
 * Executes action_actor(createMany) in dry-run or apply mode.
 */
export async function executeCreateManyAction(input: {
  payload: unknown;
  dryRun?: boolean;
}): Promise<ActorActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';
  const world = editorStore.scene.world;

  if (!world) {
    return blockedCreateManyResult(mode, ['Editor world is not loaded.']);
  }

  const parsed = parseCreateManyPayload(input.payload);
  if (!parsed.ok) {
    return blockedCreateManyResult(mode, parsed.errors);
  }

  const { actors, counts } = parsed.value;
  const plannedChanges = actors.map(actor => ({
    kind: 'createManyActor',
    key: actor.key,
    className: actor.className,
    name: actor.name ?? actor.className,
    componentCount: actor.components.length,
    transform: {
      position: actor.position,
      rotation: actor.rotation,
      scale: actor.scale,
    },
  }));

  if (dryRun) {
    return {
      ok: true,
      action: 'createMany',
      mode,
      status: 'planned',
      plannedChanges,
      affectedActorIds: [],
      missingActorIds: [],
      counts,
      createdActors: [],
      createdComponents: [],
      undoGroupId: null,
      undoUnavailableReason: null,
      warnings: [],
    };
  }

  const undoRedo = editorStore.undoRedoManager;
  const beforeIndex = undoRedo.getCurrentAction();
  const isInsideGroup = undoRedo.isGroupOpen;
  if (!isInsideGroup) undoRedo.beginGroup('Create Many Actors');

  const createdActors: CreatedActorMapping[] = [];
  const createdComponents: CreatedComponentMapping[] = [];
  const affectedActorIds: string[] = [];
  const warnings: string[] = [];

  try {
    for (const spec of actors) {
      const transform = buildTransform(spec);
      const actor = editorStore.scene.addObject(spec.className, transform, false, true);
      if (!actor) {
        warnings.push(`Failed to create actor "${spec.key}" (${spec.className}).`);
        continue;
      }

      if (spec.name) {
        const existingNames = nameHelpers.collectOutlinerNames(
          editorStore.scene.outliner.rootNode ?? undefined,
          { omitNodeId: actor.uuid },
        );
        const uniqueName = nameHelpers.makeUniqueName(spec.name, existingNames);
        nameHelpers.setCustomDisplayName(actor, uniqueName);
        editorStore.scene.outliner.getNodeById(actor.uuid)?.setName(uniqueName);
      }

      editorStore.scene.selectObject(actor);
      const actorAccessor = UniversalAccessor.forActor(actor.uuid);
      const rootComponentId = actor.rootComponent?.uuid ?? null;

      for (const componentSpec of spec.components) {
        const parentComponentId = componentSpec.parentComponentId ?? rootComponentId;
        const parentAccessor = parentComponentId
          ? UniversalAccessor.forComponent(actor.uuid, parentComponentId)
          : actorAccessor;
        const componentId = ENGINE.shortUUID();
        const added = objectActions.addComponentInternal(parentAccessor, componentSpec.className, false, componentId);
        if (!added) {
          warnings.push(`Failed to add component "${componentSpec.key}" (${componentSpec.className}) to actor "${spec.key}".`);
          continue;
        }
        const changedPaths = setComponentProperties(added, componentSpec.properties);
        if (changedPaths.length < Object.keys(componentSpec.properties).length) {
          warnings.push(`Some properties were not applied on actor "${spec.key}" component "${componentSpec.key}".`);
        }
        createdComponents.push({
          actorKey: spec.key,
          componentKey: componentSpec.key,
          componentId: added.uuid,
        });
      }

      createdActors.push({
        key: spec.key,
        actorId: actor.uuid,
        rootComponentId,
      });
      affectedActorIds.push(actor.uuid);
    }
  } finally {
    if (!isInsideGroup) undoRedo.endGroup();
    editorStore.scene.updateInspector();
  }

  if (createdActors.length === 0) {
    return blockedCreateManyResult(mode, ['No actors were created from the payload.'], counts);
  }

  const undo = (() => {
    const afterIndex = undoRedo.getCurrentAction();
    const didCreateUndo = afterIndex > beforeIndex;
    return {
      undoGroupId: didCreateUndo ? `history-${afterIndex}` : null,
      undoUnavailableReason: didCreateUndo ? null : 'Create many did not register an undo entry.',
    };
  })();

  const result: ActorActionResult = {
    ok: true,
    action: 'createMany',
    mode,
    status: 'applied',
    plannedChanges,
    affectedActorIds,
    missingActorIds: [],
    counts: {
      actors: createdActors.length,
      components: createdComponents.length,
      properties: counts.properties,
    },
    createdActors,
    createdComponents,
    undoGroupId: undo.undoGroupId,
    undoUnavailableReason: undo.undoUnavailableReason,
    warnings,
  };

  return result;
}
